import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, useRef } from "react";
import { CheckCircle2, XCircle, Loader2, Mail, RefreshCw, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/verify-email")({
  component: VerifyEmailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || "",
    email: (search.email as string) || "",
  }),
  head: () => ({
    meta: [
      { title: "Verifica tu correo — SmsEmpire" },
      {
        name: "description",
        content: "Verifica tu dirección de correo electrónico para acceder a SmsEmpire.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type VerifyState = "idle" | "loading" | "success" | "error";

const RESEND_COOLDOWN_SECS = 60;

function VerifyEmailPage() {
  const { token, email } = Route.useSearch();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { t } = useI18n();

  const [state, setState] = useState<VerifyState>(token ? "loading" : "idle");
  const [errorMsg, setErrorMsg] = useState("");

  // Resend state
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Polling ref for cross-device auto-login
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Verify token on mount ────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    const verify = async () => {
      try {
        const res = await fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`);
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data.token) {
            // Auto-login immediately in this browser
            localStorage.setItem("vh_token", data.token);
            await refreshUser();
            toast.success(t("verify.welcome_toast"));
            navigate({ to: "/dashboard" });
          } else {
            setState("success");
          }
        } else {
          const data = await res.json().catch(() => ({}));
          setErrorMsg(
            data.detail === "TOKEN_EXPIRED" ? t("verify.link_expired") : t("verify.link_invalid"),
          );
          setState("error");
        }
      } catch {
        setErrorMsg(t("verify.network_error_retry"));
        setState("error");
      }
    };
    verify();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Poll for cross-device verification (every 5s while idle) ─────────────
  useEffect(() => {
    if (state !== "idle" || !email) return;

    const poll = async () => {
      try {
        const res = await fetch(`/api/auth/verification-status?email=${encodeURIComponent(email)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.verified) return;

        if (pollTimer.current) clearInterval(pollTimer.current);

        if (data.auto_login_token) {
          // OTL consumed → auto-login this browser
          localStorage.setItem("vh_token", data.auto_login_token);
          await refreshUser();
          toast.success(t("verify.welcome_toast"));
          navigate({ to: "/dashboard" });
        } else {
          // Already verified, OTL already consumed (e.g. page refresh after first detection)
          toast.success(t("verify.already_verified"));
          navigate({ to: "/auth", search: { tab: "login" } });
        }
      } catch {
        // Ignore transient network errors
      }
    };

    poll();
    pollTimer.current = setInterval(poll, 5000);

    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [state, email]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Cooldown countdown ───────────────────────────────────────────────────
  useEffect(() => {
    if (cooldown <= 0) return;
    cooldownTimer.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(cooldownTimer.current!);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, [cooldown]);

  // ─── Resend handler ───────────────────────────────────────────────────────
  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || cooldown > 0 || resending) return;
    setResending(true);
    try {
      const res = await fetch("/api/auth/resend-verification", {
        method: "POST",
      });
      if (res.ok) {
        setResendSent(true);
        setCooldown(RESEND_COOLDOWN_SECS);
        toast.success(t("verify.resent_toast"));
      } else {
        toast.error(t("verify.resend_failed"));
      }
    } catch {
      toast.error(t("verify.network_error"));
    }
    setResending(false);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background p-4">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div
          className="absolute -top-40 left-1/2 h-[600px] w-[600px] -translate-x-1/2 rounded-full opacity-20 blur-3xl"
          style={{
            background: "radial-gradient(circle, #6366f1 0%, #8b5cf6 50%, transparent 80%)",
          }}
        />
        <div
          className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full opacity-10 blur-3xl"
          style={{ background: "radial-gradient(circle, #06b6d4 0%, transparent 70%)" }}
        />
      </div>

      <div className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-primary shadow-glow">
            <Mail className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold">SmsEmpire</h1>
        </div>

        <div className="rounded-2xl border border-border bg-card p-8 shadow-elegant text-center">
          {/* === LOADING === */}
          {state === "loading" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="relative">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                  <Loader2 className="h-10 w-10 animate-spin text-primary" />
                </div>
                <div className="absolute inset-0 rounded-full animate-pulse bg-primary/5" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">{t("verify.verifying")}</h2>
              <p className="text-sm text-muted-foreground">{t("verify.please_wait")}</p>
            </div>
          )}

          {/* === SUCCESS (fallback if auto-login unavailable) === */}
          {state === "success" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="relative">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/10">
                  <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                </div>
                <div
                  className="absolute inset-0 rounded-full animate-ping bg-emerald-500/10"
                  style={{ animationDuration: "2s" }}
                />
              </div>
              <h2 className="text-xl font-semibold text-foreground">
                {t("verify.verified_title")}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("verify.verified_desc")}
              </p>
              <Button
                className="mt-2 w-full bg-gradient-primary shadow-glow gap-2"
                onClick={() => navigate({ to: "/auth", search: { tab: "login" } })}
              >
                {t("verify.login")} <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* === ERROR === */}
          {state === "error" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="h-10 w-10 text-destructive" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">{t("verify.invalid_title")}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {errorMsg || t("verify.link_invalid")}
              </p>
              {email && (
                <div className="w-full border-t border-border pt-6 mt-2">
                  <p className="text-sm text-muted-foreground mb-4">{t("verify.need_new_link")}</p>
                  <ResendSection
                    email={email}
                    resending={resending}
                    resendSent={resendSent}
                    cooldown={cooldown}
                    onSubmit={handleResend}
                  />
                </div>
              )}
            </div>
          )}

          {/* === IDLE — waiting for email click === */}
          {state === "idle" && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="relative">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                  <Mail className="h-10 w-10 text-primary" />
                </div>
                <span
                  className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-primary text-xs font-bold text-white shadow-glow"
                  style={{ fontSize: "14px" }}
                >
                  ✉
                </span>
              </div>

              <h2 className="text-xl font-semibold text-foreground">{t("verify.check_inbox")}</h2>

              {email && (
                <div className="flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-primary/60" />
                  <span className="text-sm font-medium text-primary/80 font-mono">{email}</span>
                </div>
              )}

              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
                {t("verify.sent_desc")}
              </p>

              {/* Steps */}
              <div className="w-full rounded-xl border border-border bg-muted/30 p-4 text-left space-y-3 mt-1">
                {[
                  { step: "1", text: t("verify.step1") },
                  { step: "2", text: t("verify.step2") },
                  { step: "3", text: t("verify.step3") },
                ].map(({ step, text }) => (
                  <div key={step} className="flex items-center gap-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-bold text-primary">
                      {step}
                    </div>
                    <span className="text-sm text-foreground">{text}</span>
                  </div>
                ))}
              </div>

              {/* Auto-detection indicator */}
              <div className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground w-full justify-center">
                <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                <span>{t("verify.auto_detecting")}</span>
              </div>

              <div className="w-full border-t border-border pt-5 mt-1">
                <p className="text-sm text-muted-foreground mb-4">{t("verify.not_received")}</p>
                <ResendSection
                  email={email}
                  resending={resending}
                  resendSent={resendSent}
                  cooldown={cooldown}
                  onSubmit={handleResend}
                />
              </div>

              <Link
                to="/auth"
                className="text-xs text-muted-foreground hover:text-primary transition-colors mt-1"
              >
                ← {t("verify.back_to_login")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Resend section: email read-only, cooldown enforced ───────────────────────

function ResendSection({
  email,
  resending,
  resendSent,
  cooldown,
  onSubmit,
}: {
  email: string;
  resending: boolean;
  resendSent: boolean;
  cooldown: number;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const isDisabled = resending || cooldown > 0 || !email;
  const { t } = useI18n();

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      {/* Read-only email — div, not input, cannot be changed */}
      <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm">
        <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="font-mono text-muted-foreground truncate">{email || "—"}</span>
      </div>

      {resendSent && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
          <span className="text-sm text-emerald-500 font-medium">{t("verify.resent")}</span>
        </div>
      )}

      <Button
        type="submit"
        variant="outline"
        size="sm"
        disabled={isDisabled}
        className="w-full gap-2 border-primary/30 text-primary hover:bg-primary/10 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {resending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <RefreshCw className="h-3.5 w-3.5" />
        )}
        {resending
          ? t("verify.sending")
          : cooldown > 0
            ? t("verify.resend_in", { seconds: cooldown })
            : t("verify.resend")}
      </Button>
    </form>
  );
}
